/**
 * Bank-account date-of-death value arithmetic (VALE-802).
 *
 * A bank asked to report as at the date of death states two figures: the account
 * balance, and the credit interest the deceased had earned but had not yet been paid.
 * The value of the asset on that date is the sum, and `valueAtDateOfDeath` — the single
 * figure every downstream consumer reads — is that sum.
 *
 * Exactly two components, deliberately not the nursing-home bond's five: held credit is
 * an aged-care concept, and fees and retention are deductions from a *refund*, which on a
 * bank account land in `currentBalance` post-death.
 *
 * Mirrors `backend/src/matters/inventory-items/utils/bank-account-value.util.ts` — the two
 * packages share no code, so keep them in step when either changes.
 */

const ADDITIVE_KEYS = [
  'statedBalanceAtDateOfDeath',
  'accruedInterestAtDateOfDeath'
] as const;

export const BANK_ACCOUNT_DOD_COMPONENT_KEYS = [...ADDITIVE_KEYS] as const;

export type BankAccountDodComponents = Partial<
  Record<(typeof BANK_ACCOUNT_DOD_COMPONENT_KEYS)[number], unknown>
>;

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

/** Is any component recorded? Distinguishes a broken-down value from a bare total. */
export function hasBankAccountDodComponents(
  itemInfo: BankAccountDodComponents
): boolean {
  return BANK_ACCOUNT_DOD_COMPONENT_KEYS.some(
    (key) => asNumber(itemInfo[key]) !== undefined
  );
}

/**
 * The date-of-death value, derived from the recorded components.
 *
 * `undefined` when no component is recorded — there is nothing to derive from, so a total
 * entered directly is left alone. Absent components count as zero, so an account with
 * only a stated balance derives back to that balance.
 */
export function computeBankAccountDodValue(
  itemInfo: BankAccountDodComponents
): number | undefined {
  if (!hasBankAccountDodComponents(itemInfo)) return undefined;

  const total = ADDITIVE_KEYS.reduce(
    (sum, key) => sum + (asNumber(itemInfo[key]) ?? 0),
    0
  );

  // Currency: keep to cents so repeated derivations don't accumulate float drift.
  return Math.round(total * 100) / 100;
}

/**
 * Does the recorded date-of-death value disagree with what the components add up to?
 *
 * Drives the form's reconciliation hint. `undefined` when there is nothing to compare —
 * no components, or no total — so the hint never fires on a partially-filled account.
 */
export function isBankAccountDodValueReconciled(
  itemInfo: BankAccountDodComponents & { valueAtDateOfDeath?: unknown }
): boolean | undefined {
  const derived = computeBankAccountDodValue(itemInfo);
  const recorded = asNumber(itemInfo.valueAtDateOfDeath);
  if (derived === undefined || recorded === undefined) return undefined;
  // Tolerance of half a cent — the figures are currency, not floats to compare exactly.
  return Math.abs(derived - recorded) < 0.005;
}
